import {db, auth} from '../db';
import firebase from 'firebase';

// Helper function
function err(e) {
  return {success: false, error: e};
}

/**
 * Creates or retrieves a conversation between a tester and developer
 * Stores messages and metadata for the chat
 */
async function getOrCreateConversation(testerId, developerId) {
  return new Promise(async (resolve, reject) => {
    try {
      // Create a unique conversation ID (always in same order for consistency)
      const conversationId = [testerId, developerId].sort().join('_');
      
      const conversationRef = db.collection("Conversations").doc(conversationId);
      
      conversationRef.get()
        .then(doc => {
          if (doc.exists) {
            // Conversation already exists
            resolve({success: true, data: {conversationId, ...doc.data()}});
          } else {
            // Create new conversation
            const conversationData = {
              participants: [testerId, developerId],
              createdAt: new Date(),
              lastMessageAt: new Date(),
              lastMessage: "",
              unreadCount: {[testerId]: 0, [developerId]: 0},
              acceptedStatus: "pending",
            };
            
            conversationRef.set(conversationData)
              .then(() => {
                resolve({success: true, data: {conversationId, ...conversationData}});
              })
              .catch(e => reject(err(e)));
          }
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Accepts a conversation
 */
async function acceptConversation(conversationId) {
  try{
    await db.collection("Conversations")
        .doc(conversationId)
        .update({acceptedStatus: "accepted"});
    return {success: true}
  } catch (e) {
    return {success: false, error: e};
  }
}

/*
  Raises an admin notification when a non-admin messages an admin directly.
  Runs alongside the send rather than blocking it.
*/
function notifyAdminOfMessage(conversationId, senderId, messageText) {
  db.collection("Conversations").doc(conversationId).get()
    .then(async convDoc => {
      if (!convDoc.exists || convDoc.data().isAdminChat) return;

      const senderDoc = await db.collection("User").doc(senderId).get();
      if (!senderDoc.exists || senderDoc.data().userType === "admin") return;

      const recipients = (convDoc.data().participants || []).filter(id => id !== senderId);
      for (const recipientId of recipients) {
        const recipientDoc = await db.collection("User").doc(recipientId).get();
        if (!recipientDoc.exists || recipientDoc.data().userType !== "admin") continue;

        await db.collection("Notification").doc().set({
          userId: recipientId,
          type: "new-message",
          title: "New message",
          content: messageText.length > 120 ? `${messageText.slice(0, 120)}...` : messageText,
          read: false,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          senderName:
            senderDoc.data().userName || senderDoc.data().nickName || senderDoc.data().email || "Unknown user",
          senderId,
          conversationId,
        });
      }
    })
    .catch(e => console.log(e));
}

/**
 * Sends a message in a conversation
 */
async function sendMessage(conversationId, senderId, messageText, messageType = "text") {
  return new Promise((resolve, reject) => {
    try {
      if (!messageText || messageText.trim() === "") {
        reject({success: false, error: "Message cannot be empty"});
        return;
      }

      const messageData = {
        senderId: senderId,
        messageText: messageText.trim(),
        messageType: messageType, // "text", "image", "file", etc.
        timestamp: new Date(),
        read: false,
      };

      db.collection("Conversations")
        .doc(conversationId)
        .collection("Messages")
        .add(messageData)
        .then(docRef => {
          // Update conversation's lastMessage and lastMessageAt
          db.collection("Conversations")
            .doc(conversationId)
            .update({
              lastMessage: messageText,
              lastMessageAt: new Date(),
            })
            .then(() => {
              notifyAdminOfMessage(conversationId, senderId, messageText);
              resolve({
                success: true,
                data: {
                  messageId: docRef.id,
                  ...messageData,
                },
              });
            })
            .catch(e => reject(err(e)));
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Retrieves all messages from a conversation, with real-time listener option
 */
async function getMessages(conversationId, limit = 50, realtimeCallback = null) {
  return new Promise((resolve, reject) => {
    try {
      const messagesRef = db.collection("Conversations")
        .doc(conversationId)
        .collection("Messages")
        .orderBy("timestamp", "asc")
        .limitToLast(limit);

      if (realtimeCallback) {
        // Set up real-time listener
        const unsubscribe = messagesRef.onSnapshot(
          snapshot => {
            const messages = [];
            snapshot.forEach(doc => {
              messages.push({
                messageId: doc.id,
                ...doc.data(),
              });
            });
            realtimeCallback({success: true, data: messages});
          },
          e => realtimeCallback({success: false, error: e})
        );
        
        // Return unsubscribe function so caller can stop listening
        resolve({
          success: true,
          unsubscribe: unsubscribe,
        });
      } else {
        // One-time fetch
        messagesRef.get()
          .then(snapshot => {
            const messages = [];
            snapshot.forEach(doc => {
              messages.push({
                messageId: doc.id,
                ...doc.data(),
              });
            });
            resolve({success: true, data: messages});
          })
          .catch(e => reject(err(e)));
      }
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Retrieves all conversations for a user
 */
async function getUserConversations(userId, realtimeCallback = null) {
  return new Promise((resolve, reject) => {
    try {
      const conversationsRef = db.collection("Conversations")
        .where("participants", "array-contains", userId)
        .orderBy("lastMessageAt", "desc");

      if (realtimeCallback) {
        // Set up real-time listener
        const unsubscribe = conversationsRef.onSnapshot(
          snapshot => {
            const conversations = [];
            snapshot.forEach(doc => {
              conversations.push({
                conversationId: doc.id,
                ...doc.data(),
              });
            });
            realtimeCallback({success: true, data: conversations});
          },
          e => realtimeCallback({success: false, error: e})
        );
        
        resolve({
          success: true,
          unsubscribe: unsubscribe,
        });
      } else {
        // One-time fetch
        conversationsRef.get()
          .then(snapshot => {
            const conversations = [];
            snapshot.forEach(doc => {
              conversations.push({
                conversationId: doc.id,
                ...doc.data(),
              });
            });
            resolve({success: true, data: conversations});
          })
          .catch(e => reject(err(e)));
      }
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Marks a message as read
 */
async function markMessageAsRead(conversationId, messageId) {
  return new Promise((resolve, reject) => {
    try {
      db.collection("Conversations")
        .doc(conversationId)
        .collection("Messages")
        .doc(messageId)
        .update({read: true})
        .then(() => {
          resolve({success: true});
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

async function markNotificationAsRead(userId, notificationId) {
  return new Promise((resolve, reject) => {
    try {
      db.collection("UserNotifications")
        .doc(userId)
        .collection("Notifications")
        .doc(notificationId)
        .update({ unread: false })
        .then(() => {
          resolve({ success: true });
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Marks all messages in a conversation as read for a specific user
 */
async function markConversationAsRead(conversationId, userId) {
  return new Promise((resolve, reject) => {
    try {
      db.collection("Conversations")
        .doc(conversationId)
        .collection("Messages")
        .where("senderId", "!=", userId)
        .where("read", "==", false)
        .get()
        .then(snapshot => {
          const batch = db.batch();
          snapshot.forEach(doc => {
            batch.update(doc.ref, {read: true});
          });
          return batch.commit();
        })
        .then(() => {
          // Update conversation unread count
          db.collection("Conversations")
            .doc(conversationId)
            .update({
              [`unreadCount.${userId}`]: 0,
            })
            .then(() => resolve({success: true}))
            .catch(e => reject(err(e)));
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Gets unread message count for a user across all conversations
 */
async function getUnreadCount(userId) {
  return new Promise((resolve, reject) => {
    try {
      db.collection("Conversations")
        .where("participants", "array-contains", userId)
        .get()
        .then(snapshot => {
          let totalUnread = 0;
          snapshot.forEach(doc => {
            const unreadCount = doc.data().unreadCount || {};
            totalUnread += unreadCount[userId] || 0;
          });
          resolve({success: true, data: totalUnread});
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Deletes a conversation (soft delete - could archive instead)
 */
async function deleteConversation(conversationId) {
  return new Promise((resolve, reject) => {
    try {
      db.collection("Conversations")
        .doc(conversationId)
        .delete()
        .then(() => {
          resolve({success: true});
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Searches messages within a conversation
 */
async function searchMessages(conversationId, searchTerm) {
  return new Promise((resolve, reject) => {
    try {
      // Note: Firestore doesn't support full-text search directly
      // This is a client-side search solution
      db.collection("Conversations")
        .doc(conversationId)
        .collection("Messages")
        .get()
        .then(snapshot => {
          const results = [];
          snapshot.forEach(doc => {
            const message = doc.data();
            if (message.messageText.toLowerCase().includes(searchTerm.toLowerCase())) {
              results.push({
                messageId: doc.id,
                ...message,
              });
            }
          });
          resolve({success: true, data: results});
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

async function deleteMessage(conversationId, messageId) {
  return db
    .collection("Conversations")
    .doc(conversationId)
    .collection("Messages")
    .doc(messageId)
    .delete()
    .then(() => ({ success: true }))
    .catch((error) => ({ success: false, error }));
}

/**
 * Gets a specific user's info for chat display
 */
async function getUserInfo(userId) {
  return new Promise((resolve, reject) => {
    try {
      db.collection("User")
        .doc(userId)
        .get()
        .then(doc => {
          if (!doc.exists) {
            reject({success: false, error: "User not found"});
          } else {
            resolve({
              success: true,
              data: {
                userId: doc.id,
                userName: doc.data().userName || "",
                nickName: doc.data().nickName || "",
                img: doc.data().img || "",
                email: doc.data().email || "",
              },
            });
          }
        })
        .catch(e => reject(err(e)));
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * ============================================
 * ADMIN ANNOUNCEMENT FUNCTIONS
 * ============================================
 */

/**
 * Sends an announcement from admin to all users
 * Creates/updates an "admin_announcements" conversation for each user
 */
async function sendAdminAnnouncement(adminId, announcementText) {
  return new Promise(async (resolve, reject) => {
    try {
      if (!announcementText || announcementText.trim() === "") {
        reject({success: false, error: "Announcement cannot be empty"});
        return;
      }

      // Get all users (both testers and developers)
      const usersSnapshot = await db.collection("User").get();
      
      const batch = db.batch();
      const timestamp = new Date();
      let successCount = 0;
      let errorCount = 0;

      const promises = [];

      usersSnapshot.forEach(userDoc => {
        const userId = userDoc.id;
        
        // Skip the admin themselves
        if (userId === adminId) return;

        const promise = (async () => {
          try {
            // Create conversation ID: admin_{userId}
            const conversationId = `admin_${userId}`;
            const conversationRef = db.collection("Conversations").doc(conversationId);
            
            // Check if conversation exists
            const convDoc = await conversationRef.get();
            
            if (!convDoc.exists) {
              // Create new admin conversation
              await conversationRef.set({
                participants: [adminId, userId],
                isAdminChat: true,
                createdAt: timestamp,
                lastMessageAt: timestamp,
                lastMessage: announcementText.substring(0, 100),
                unreadCount: {[adminId]: 0, [userId]: 1},
                pinned: true
              });
            } else {
              // Update existing conversation
              const currentUnread = convDoc.data().unreadCount || {};
              await conversationRef.update({
                lastMessageAt: timestamp,
                lastMessage: announcementText.substring(0, 100),
                [`unreadCount.${userId}`]: firebase.firestore.FieldValue.increment(1)
              });
            }

            // Add message to subcollection
            const messageRef = conversationRef.collection("Messages").doc();
            await messageRef.set({
              senderId: adminId,
              messageText: announcementText.trim(),
              messageType: "announcement",
              timestamp: timestamp,
              read: false,
              isAnnouncement: true
            });

            successCount++;
          } catch (error) {
            console.error(`Error sending announcement to user ${userId}:`, error);
            errorCount++;
          }
        })();

        promises.push(promise);
      });

      await Promise.all(promises);

      resolve({
        success: true,
        data: {
          totalUsers: usersSnapshot.size - 1, // Exclude admin
          successCount,
          errorCount,
          timestamp
        }
      });

    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Gets the admin chat conversation for a specific user
 * Returns null if no admin chat exists yet
 */
async function getAdminChatForUser(userId, realtimeCallback = null) {
  return new Promise((resolve, reject) => {
    try {
      const conversationId = `admin_${userId}`;
      const conversationRef = db.collection("Conversations").doc(conversationId);

      if (realtimeCallback) {
        // Set up real-time listener
        const unsubscribe = conversationRef.onSnapshot(
          doc => {
            if (doc.exists) {
              realtimeCallback({
                success: true,
                data: {
                  conversationId: doc.id,
                  ...doc.data()
                }
              });
            } else {
              realtimeCallback({success: true, data: null});
            }
          },
          e => realtimeCallback({success: false, error: e})
        );
        
        resolve({
          success: true,
          unsubscribe: unsubscribe,
        });
      } else {
        // One-time fetch
        conversationRef.get()
          .then(doc => {
            if (doc.exists) {
              resolve({
                success: true,
                data: {
                  conversationId: doc.id,
                  ...doc.data()
                }
              });
            } else {
              resolve({success: true, data: null});
            }
          })
          .catch(e => reject(err(e)));
      }
    } catch (e) {
      reject(err(e));
    }
  });
}

/**
 * Gets announcement history (admin messages) for a user
 */
async function getAdminAnnouncements(userId, limit = 50, realtimeCallback = null) {
  const conversationId = `admin_${userId}`;
  return getMessages(conversationId, limit, realtimeCallback);
}

export default {
  getOrCreateConversation,
  sendMessage,
  getMessages,
  getUserConversations,
  markMessageAsRead,
  markConversationAsRead,
  getUnreadCount,
  deleteConversation,
  searchMessages,
  getUserInfo,
  acceptConversation,
  deleteMessage,
  markNotificationAsRead,
  // Admin announcement functions
  sendAdminAnnouncement,
  getAdminChatForUser,
  getAdminAnnouncements
};


// WEBPACK FOOTER //
// ./src/Firestore/Chat.js